/**
 * InsightsLiveList (F9 — E.15) — dashboard read-only.
 *
 * Lista de insights renderizada para manager/admin que está observando
 * uma reunião ao vivo. Sem botões de feedback/dismiss (o overlay do
 * closer é o único que pode reagir). Acesso já é gateado no backend
 * (close 4403 para closer comum).
 *
 * Recebe os `Insight`s já filtrados/ordenados pelo caller; este
 * componente é puramente de apresentação.
 */

import type { Insight } from '@copilot/shared-types';

const SEVERITY_COLOR: Record<Insight['severity'], string> = {
  high: 'var(--color-danger, #ff6b6b)',
  medium: 'var(--color-accent, #ffa726)',
  low: 'var(--color-border, #888)',
};

const CATEGORY_LABEL: Record<string, string> = {
  preco: 'Preço',
  tecnica: 'Técnica',
  concorrente: 'Concorrente',
  talk_ratio: 'Talk ratio',
  monologo: 'Monólogo',
  interesse_alto: 'Interesse alto',
  desinteresse: 'Desinteresse',
  mudanca_sentimento: 'Mudança de sentimento',
  pedido_dado: 'Pedido de dado',
};

export interface InsightsLiveListProps {
  insights: readonly Insight[];
}

export function InsightsLiveList({ insights }: InsightsLiveListProps) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <header>
        <h2 style={{ margin: 0, fontSize: '1rem' }}>Insights ao vivo</h2>
      </header>
      {insights.length === 0 ? (
        <p style={{ opacity: 0.6, margin: 0 }}>Nenhum insight emitido ainda.</p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
          }}
        >
          {insights.map((ins) => (
            <li
              key={ins.id}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: 8,
                borderLeft: `3px solid ${SEVERITY_COLOR[ins.severity]}`,
                background: 'var(--surface-2, rgba(255, 255, 255, 0.04))',
              }}
            >
              <div
                style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <strong>{ins.title}</strong>
                <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                  {CATEGORY_LABEL[ins.category] ?? ins.category}
                </span>
                <span style={{ fontSize: '0.75rem', opacity: 0.7 }}>{ins.severity}</span>
              </div>
              {ins.body !== null && ins.body !== undefined && ins.body.length > 0 ? (
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', opacity: 0.85 }}>
                  {ins.body}
                </p>
              ) : null}
              {ins.source !== null && ins.source !== undefined ? (
                <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', opacity: 0.7 }}>
                  <strong>{ins.source.document}</strong> · {ins.source.category}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
